import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { dataHoraBrasilia, estaAgendado } from "@/lib/admin/agenda";
import { montarChecklist, nivel, type DadosChecklist } from "@/lib/admin/checklist";

type Linha = DadosChecklist & {
  id: string;
  slug: string;
  status: string;
  published_at: string | null;
  updated_at: string | null;
  category_id: string | null;
  post_categories: { label: string } | null;
};

type Aba = "todos" | "publicado" | "rascunho";

export const Route = createFileRoute("/admin/")({
  ssr: false,
  head: () => ({ meta: [{ title: "Artigos | Painel" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ListaArtigos,
});

function formatar(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

function ListaArtigos() {
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [categorias, setCategorias] = useState<{ id: string; label: string }[]>([]);
  const [aba, setAba] = useState<Aba>("todos");
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("");

  useEffect(() => {
    void supabase
      .from("posts")
      .select("id, title, seo_title, slug, status, published_at, updated_at, category_id, direct_answer, body, meta_description, reviewed_at, cover_media_id, faq, references, post_categories:category_id (label)")
      .order("updated_at", { ascending: false })
      .then(({ data }) => setLinhas((data ?? []) as unknown as Linha[]));
    void supabase
      .from("post_categories")
      .select("id, label")
      .order("sort_order")
      .then(({ data }) => setCategorias(data ?? []));
  }, []);

  const todas = linhas ?? [];
  const contagem = {
    todos: todas.length,
    publicado: todas.filter((l) => l.status === "publicado").length,
    rascunho: todas.filter((l) => l.status !== "publicado").length,
  };

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return todas.filter((l) => {
      if (aba === "publicado" && l.status !== "publicado") return false;
      if (aba === "rascunho" && l.status === "publicado") return false;
      if (categoria && l.category_id !== categoria) return false;
      if (termo && !`${l.title ?? ""} ${l.slug}`.toLowerCase().includes(termo)) return false;
      return true;
    });
  }, [todas, aba, busca, categoria]);

  const abas: { chave: Aba; rotulo: string }[] = [
    { chave: "todos", rotulo: "Todos" },
    { chave: "publicado", rotulo: "Publicados" },
    { chave: "rascunho", rotulo: "Rascunhos" },
  ];

  return (
    <div className="admin-page">

      <div className="admin-page-head">
        <div>
          <h1>Artigos</h1>
          <p>Crie, revise e publique os conteúdos do blog.</p>
        </div>
        <Link to="/admin/artigos/novo/" className="admin-btn" data-variant="primary" data-size="lg">
          <Plus aria-hidden="true" /> Novo artigo
        </Link>
      </div>

      <div className="admin-tabs" role="tablist">
        {abas.map((a) => (
          <button key={a.chave} type="button" role="tab" aria-selected={aba === a.chave} className="admin-tab" data-active={aba === a.chave} onClick={() => setAba(a.chave)}>
            {a.rotulo}
            <span>{contagem[a.chave]}</span>
          </button>
        ))}
      </div>

      <div className="admin-filters">
        <label className="admin-search">
          <Search aria-hidden="true" />
          <input aria-label="Buscar artigos" placeholder="Buscar por título ou endereço" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </label>
        <select className="admin-select" aria-label="Filtrar por categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          <option value="">Categoria: todas</option>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      {linhas === null ? (
        <p className="admin-foot-note">Carregando...</p>
      ) : (
        <>
          <div className="admin-grid-table">
            <div className="admin-grid-table-inner">
              <div className="admin-grid-row" data-head="true">
                <span>Título</span><span>Categoria</span><span>Status</span><span>Checklist</span><span>Publicação</span><span />
              </div>
              {visiveis.length === 0 ? (
                <div className="admin-grid-row"><span className="admin-cell-muted">Nenhum artigo encontrado.</span></div>
              ) : (
                visiveis.map((linha) => {
                  const itens = montarChecklist(linha);
                  const feitos = itens.filter((i) => i.feito).length;
                  return (
                    <Link key={linha.id} to="/admin/artigos/$id/" params={{ id: linha.id }} className="admin-grid-row">
                      <span className="admin-cell-title">
                        <strong>{linha.title || "Sem título"}</strong>
                        <span>/blog/{linha.slug}</span>
                      </span>
                      <span className="admin-cell-muted">{linha.post_categories?.label ?? "—"}</span>
                      <span>
                        {estaAgendado(linha.status, linha.published_at) ? (
                          <span className="admin-status" data-scheduled="true">Agendado</span>
                        ) : (
                          <span className="admin-status" data-published={linha.status === "publicado"}>{linha.status === "publicado" ? "Publicado" : "Rascunho"}</span>
                        )}
                      </span>
                      <span className="admin-meter">
                        <span className="admin-meter-track"><span className="admin-meter-fill" data-level={nivel(feitos, itens.length)} style={{ width: `${(feitos / itens.length) * 100}%` }} /></span>
                        <span className="admin-cell-mono">{feitos}/{itens.length}</span>
                      </span>
                      <span className="admin-cell-mono">{estaAgendado(linha.status, linha.published_at) ? dataHoraBrasilia(linha.published_at!) : formatar(linha.published_at ?? linha.updated_at)}</span>
                      <span className="admin-cell-action">Editar <ArrowRight aria-hidden="true" /></span>
                    </Link>
                  );
                })
              )}
            </div>
          </div>
          <p className="admin-foot-note">Mostrando {visiveis.length} de {todas.length} artigos</p>
        </>
      )}
    </div>
  );
}
